import type {
  CloudinaryAsset,
  SanityImage as ISanityImage,
} from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import {
  blurUrl,
  calculateHeight,
  isCloudinaryAsset,
  isSanityImage,
  urlFor,
} from '@bulknutrients/bulk-shared-lib/lib/images';
import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import Image from 'next/image';
import { getCldImageUrl } from 'next-cloudinary';
import type { ReactElement, ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

// #region Type Definitions
interface BaseProps {
  showOverlay?: boolean;
  usePlaceholder?: boolean;
  round?: boolean;
  priority?: boolean;
  alt?: string;
  sizes?: string;
  title?: string;
  className?: string;
  width: number; // rendered size, not intrinsic size
  height?: number; // rendered size, not intrinsic size. If not provided, will be calculated from width and image ratio
}

interface SrcProps extends BaseProps {
  image?: never;
  src: string;
  alt: string;
}

interface CloudinaryImageProps extends BaseProps {
  image: CloudinaryAsset;
  templateOverride?: Record<string, string | number>;
  src?: never;
}

interface SanityImageProps extends BaseProps {
  image: ISanityImage;
  height?: number;
  src?: never;
}

type Props = SrcProps | CloudinaryImageProps | SanityImageProps;
// #endregion

// #region Image URL Generators
const getCloudinaryImageSrcUrl = ({
  image,
  width,
  height,
  templateOverride,
}: {
  image: CloudinaryAsset;
  width: number;
  height?: number;
  templateOverride?: Record<string, string | number>;
}): string => {
  const quality = 90;
  // Fixed at 2 to keep server and client renders identical and prevent hydration mismatches.
  // Requesting 2x from Sanity is a safe default; Next.js <Image> handles responsive srcsets separately.
  const dpr = 2;

  try {
    return templateOverride
      ? getCldImageUrl({
          format: 'webp',
          quality: quality,
          src: image.asset.public_id,
          dpr: dpr,
          width: width,
          height: height ? height : undefined,
          ...templateOverride,
        })
      : getCldImageUrl({
          format: 'webp',
          quality: quality,
          src: image.asset.public_id,
          dpr: dpr,
          rawTransformations:
            image.asset.derived?.map((t) => t.raw_transformation) ?? [],
        });
  } catch (err) {
    console.error(err);
    return '';
  }
};

const getSanityImageSrcUrl = ({
  image,
  width,
  height,
}: {
  image: ISanityImage;
  width: number;
  height?: number;
}): string => {
  const quality = 90;
  // Fixed at 2 to keep server and client renders identical and prevent hydration mismatches.
  // Requesting 2x from Sanity is a safe default; Next.js <Image> handles responsive srcsets separately.
  const dpr = 2;

  try {
    if (height && image.hotspot) {
      return urlFor(image.asset)
        .auto('format')
        .width(width * dpr)
        .height(height * dpr)
        .fit('crop')
        .crop('focalpoint')
        .focalPoint(image.hotspot.x, image.hotspot.y)
        .quality(quality)
        .url() as string;
    }
    if (height) {
      return urlFor(image.asset)
        .auto('format')
        .width(width * dpr)
        .height(height * dpr)
        .fit('max')
        .quality(quality)
        .url() as string;
    }
    return urlFor(image.asset)
      .auto('format')
      .width(width * dpr)
      .fit('fillmax')
      .quality(quality)
      .url() as string;
  } catch (err) {
    console.error(err);
    return '';
  }
};

// Generate image url string from either Sanity or Cloudinary image object
export const getImageSrcUrl = ({
  image,
  width,
  height,
  templateOverride,
}: {
  image: ISanityImage | CloudinaryAsset;
  width: number;
  height?: number;
  templateOverride?: Record<string, string | number>;
}): string => {
  try {
    if (isCloudinaryAsset(image)) {
      return getCloudinaryImageSrcUrl({
        image,
        width,
        height,
        templateOverride,
      });
    }
    if (isSanityImage(image)) {
      return getSanityImageSrcUrl({
        image,
        width,
        height,
      });
    }
  } catch (err) {
    console.error(err);
    return '';
  }
  return '';
};
// #endregion

const getInlineStyles = (image: ISanityImage | CloudinaryAsset) => {
  const inlineStyles = [
    '[--background]:grey-mid [--border]:grey-mid dark:[--border]:grey-dark dark:[--background]:grey-dark relative mx-auto my-0 flex min-h-fit w-fit flex-col gap-1 dark:text-white',
  ] as Array<string>;

  if (image.border) {
    inlineStyles.push(
      'border border-solid border-grey-mid p-2.5! bg-grey-light dark:border-grey-dark dark:bg-grey-dark rounded-2xl'
    );
  }
  if (image.floatLeft) {
    inlineStyles.push('sm:float-left sm:mr-2.5 sm:max-w-[50%] sm:clear-both');
  }
  if (image.floatRight) {
    inlineStyles.push('sm:float-right sm:ml-2.5 sm:max-w-[50%] sm:clear-both');
  }
  return inlineStyles;
};

const OverlayMap = {
  pharmaceuticalGrade: {
    src: '/images/icon-100-pharmaceutical-grade.webp',
    alt: '100% pharmaceutical grade',
  },
  improvedFormula: {
    src: '/images/icon-improved-formula-icons.webp',
    alt: 'Now with a revised formulation',
  },
  sale: {
    src: '/images/icon-sale.webp',
    alt: 'This product is on sale',
  },
  hasta: {
    src: '/images/icon-hasta.webp',
    alt: 'This product is HASTA certified',
  },
  veganCertified: {
    src: '/images/icon-vegan-certified.webp',
    alt: 'This product is vegan certified',
  },
  plantBased: {
    src: '/images/icon-plant-based.webp',
    alt: 'This product is plant based',
  },
};

function Overlay({ image }: { image: ISanityImage | CloudinaryAsset }) {
  return (
    <div
      className={twJoin([
        'overlay',
        'absolute bottom-[1.65rem] right-0 h-[130px] w-[180px] bg-transparent',
      ])}
    >
      <Image
        src={
          OverlayMap[image.overlay?.[0] ?? ('' as keyof typeof OverlayMap)].src
        }
        alt={
          OverlayMap[image.overlay?.[0] ?? ('' as keyof typeof OverlayMap)].alt
        }
        width={180}
        height={130}
        className="mx-auto my-0 h-full max-w-full"
      />
    </div>
  );
}

function FigCaption({
  image,
  href,
}: {
  image: ISanityImage | CloudinaryAsset;
  href?: string;
}) {
  return (
    <>
      {image?.caption && href ? (
        <figcaption className="text-sm text-grey-dark dark:text-grey-light">
          <TextLink href={href}>{image.caption}</TextLink>
        </figcaption>
      ) : image?.caption ? (
        <figcaption className="text-sm text-grey-dark dark:text-grey-light">
          {image.caption}
        </figcaption>
      ) : null}
    </>
  );
}

// Check if there is a link, and add it if so
function LinkCheckWrapper({
  image,
  children,
}: {
  image?: ISanityImage | CloudinaryAsset;
  children: ReactNode;
}) {
  if (!image || typeof image === 'string') {
    return <>{children}</>;
  }

  if (image.internalLink || (image.link && !image.link.startsWith('http'))) {
    // it has a link and is internal / relative
    const href = image.internalLink ? image.internalLink : image.link;
    return (
      <TextLink
        href={href}
        target={image.newTab ? '_blank' : ''}
        rel={`noreferrer ${image.noFollow ? 'nofollow' : ''}`}
      >
        {children}
      </TextLink>
    );
  }
  if (image.link) {
    // has a link that is absolute and external
    const href = image.link;
    return (
      <a
        href={href}
        target={image.newTab ? '_blank' : ''}
        rel={`noreferrer ${image.noFollow ? 'nofollow' : ''}`}
      >
        {children}
      </a>
    );
  }
  // no link
  return <>{children}</>;
}

function StyledNextImage({
  image,
  src,
  alt,
  showOverlay,
  width,
  height,
  round = false,
  priority = false,
  sizes,
  title,
  className,
  usePlaceholder,
}: {
  image?: ISanityImage | CloudinaryAsset;
  src: string;
  alt?: string;
  showOverlay: boolean;
  width: number;
  height?: number;
  round?: boolean;
  priority?: boolean;
  sizes?: string;
  title?: string;
  className?: string;
  usePlaceholder?: boolean;
}) {
  // Add borders and floats as required. Add a min height to avoid CLS issues
  // these go on the <figure> wrapper
  const inlineStyles = image ? getInlineStyles(image) : [];

  // Create the blur url for the placeholder.
  let blurUrlSrc = blurUrl(width, height ?? width);
  // Sanity has lqip
  if (image && isSanityImage(image) && (image as ISanityImage).lqip) {
    blurUrlSrc = (image as ISanityImage).lqip as string;
  }
  // we can make cloudinary ones on the fly with a very low quality and small size
  if (image && isCloudinaryAsset(image)) {
    blurUrlSrc = getCloudinaryImageSrcUrl({
      image: image as CloudinaryAsset,
      width: 20,
      height: calculateHeight(20, image.ratio),
      templateOverride: { quality: 1 },
    });
  }

  return (
    <figure className={twJoin([...inlineStyles])}>
      <LinkCheckWrapper image={image}>
        <Image
          src={src}
          alt={image ? image.altText : (alt ?? '')}
          width={width} // intrinsic width, not CSS width
          height={height} // intrinsic height, not CSS height
          sizes={sizes} // Define the sizes of the image at different breakpoints
          preload={priority}
          placeholder={usePlaceholder || (image && src) ? 'blur' : 'empty'} // Use the blur placeholder if we have an image
          blurDataURL={blurUrlSrc}
          className={mergeStyles(
            className ?? '',
            `mx-auto my-0 h-full max-w-full ${round ? 'rounded-full' : 'rounded-2xl'}`
          )}
          unoptimized
          title={title}
        />
        {showOverlay &&
          image?.overlay?.[0] &&
          Object.keys(OverlayMap).includes(image.overlay?.[0] ?? '') && (
            <Overlay image={image} />
          )}
      </LinkCheckWrapper>

      {image && (
        <FigCaption
          image={image}
          href={image.internalLink ? image.internalLink : image.link}
        />
      )}
    </figure>
  );
}

function CloudinaryImage({ ...props }: CloudinaryImageProps) {
  return (
    <StyledNextImage
      {...props}
      src={getCloudinaryImageSrcUrl({
        image: props.image,
        width: props.width,
        height: props.height ?? calculateHeight(props.width, props.image.ratio),
        templateOverride: props.templateOverride,
      })}
      showOverlay={props.showOverlay ?? false}
    />
  );
}

function SanityImage({ ...props }: SanityImageProps) {
  return (
    <StyledNextImage
      {...props}
      src={getSanityImageSrcUrl({
        image: props.image,
        width: props.width,
        height: props.height ?? calculateHeight(props.width, props.image.ratio),
      })}
      showOverlay={props.showOverlay ?? false}
    />
  );
}

function SrcImage({ ...props }: SrcProps) {
  return (
    <StyledNextImage {...props} showOverlay={props.showOverlay ?? false} />
  );
}

export function CustomImage({ ...props }: Props): ReactElement {
  if (!props.image && !props.src) return <></>; // given nothing to render

  if (props.src) {
    return (
      <SrcImage
        src={props.src}
        alt={props.alt ?? ''}
        width={props.width}
        height={props.height ?? props.width}
        round={props.round}
      />
    );
  }

  if (props.image && isCloudinaryAsset(props.image)) {
    return (
      <CloudinaryImage
        {...props}
        image={props.image}
        height={props.height ?? calculateHeight(props.width, props.image.ratio)}
      />
    );
  }

  if (props.image && isSanityImage(props.image)) {
    return (
      <SanityImage
        {...props}
        image={props.image}
        width={props.width}
        height={props.height ?? calculateHeight(props.width, props.image.ratio)}
      />
    );
  }

  return <></>; // fallback, should never hit this because of the first check, but satisfies TypeScript that we are returning a ReactElement
}
